import { OnInit, Input, Output, EventEmitter, Directive } from '@angular/core';
import { FormGroup, FormArray, FormBuilder, FormControl } from '@angular/forms';
import { BaseRule } from '../../../models/feature-flag.model';

/**
 * Abstract base component for list-based rule types (like regions, lines of business, etc.)
 * Provides common functionality for managing lists of values
 */
@Directive()
export abstract class AbstractListRuleComponent<T extends BaseRule> implements OnInit {
  /**
   * Parent form group
   */
  @Input() parentForm: FormGroup;

  /**
   * Event emitted when the rule changes
   */
  @Output() ruleChanged = new EventEmitter<T>();

  /**
   * Form control for the new item input
   */
  newItemControl = new FormControl('');

  /**
   * Error message for validation
   */
  error: string = null;

  /**
   * The rule type identifier
   */
  protected abstract ruleType: string;

  /**
   * Common items for quick selection
   */
  protected abstract commonItems: string[];

  /**
   * Error message for duplicates
   */
  protected abstract duplicateErrorMessage: string;

  constructor(protected fb: FormBuilder) {}

  ngOnInit(): void {
    // Ensure the rule form group has a values array
    const ruleGroup = this.parentForm.get('rule') as FormGroup;
    if (!ruleGroup.contains('values')) {
      ruleGroup.addControl('values', this.fb.array([]));
    }
  }

  /**
   * Get the values form array
   */
  get valuesArray(): FormArray {
    return this.parentForm.get('rule.values') as FormArray;
  }

  /**
   * Get the items as string array
   */
  get items(): string[] {
    return this.valuesArray.value;
  }

  /**
   * Add a new item
   */
  addItem(item = this.newItemControl.value?.trim()): void {
    if (!item) {
      return;
    }

    const normalizedItem = item.trim();

    // Check for duplicates
    if (this.items.includes(normalizedItem)) {
      this.error = this.duplicateErrorMessage;
      return;
    }

    // Clear any error
    this.error = null;

    // Add to form array
    this.valuesArray.push(this.fb.control(normalizedItem));

    // Clear input
    this.newItemControl.reset();

    // Emit the updated rule
    this.emitRuleChanged();
  }

  /**
   * Remove an item
   */
  removeItem(index: number): void {
    this.valuesArray.removeAt(index);
    this.emitRuleChanged();
  }

  /**
   * Clear all items
   */
  clearItems(): void {
    while (this.valuesArray.length) {
      this.valuesArray.removeAt(0);
    }
    this.emitRuleChanged();
  }

  /**
   * Check if an item is already selected
   */
  isItemSelected(item: string): boolean {
    return this.items.includes(item);
  }

  /**
   * Toggle selection of a common item
   */
  toggleItem(item: string): void {
    if (this.isItemSelected(item)) {
      const index = this.items.indexOf(item);
      if (index !== -1) {
        this.removeItem(index);
      }
    } else {
      this.addItem(item);
    }
  }

  /**
   * Handle Enter key in the input field
   */
  onInputKeydown(event: KeyboardEvent): void {
    if (event.key === 'Enter') {
      event.preventDefault();
      this.addItem();
    }
  }

  /**
   * Create and emit the updated rule
   */
  protected emitRuleChanged(): void {
    const updatedRule = {
      type: this.ruleType,
      values: this.items,
    } as unknown as T;

    this.ruleChanged.emit(updatedRule);
  }
}
